import { Badge, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, InputBase, Link, makeStyles, Paper, Switch, Theme, Typography } from "@material-ui/core";
import blue from "@material-ui/core/colors/blue";
import { alpha } from "@material-ui/core/styles";
import AssistantIcon from '@material-ui/icons/AssistantOutlined';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DescriptionIcon from '@material-ui/icons/DescriptionOutlined';
import SendChatMessageIcon from '@material-ui/icons/Telegram';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSendAssistantMessageMutation } from "../../graphql/generated/graphql";
import useInput from "../../shared/Hooks/useInput";
import useAssistantRealtime, { ChatMessage } from "./useAssistantRealtime";

const useAssistantStyles = makeStyles((theme: Theme) =>
{
  const isLight = theme.palette.type === 'light';

  return ({
    dialogTitle: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
    },
    dialogContent: {
      padding: theme.spacing(2),
      minHeight: 'calc(100vh - 250px)',
    },
    chatMessageIcon: {
      fontSize: 20,
      marginBottom: -5,
      marginRight: 4,
      color: blue[600],
    },
    dialogActions: {
      padding: theme.spacing(2),
    },
    statusBar: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(0.75, 1),
      borderRadius: 12,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      backgroundColor: 'transparent',
    },
    statusBarText: {
      flex: 1,
    },
    statusBarLabel: {
      marginLeft: theme.spacing(0.5),
      color: theme.palette.text.secondary,
      opacity: 0.7,
    },
    statusBarIcon: {
      color: theme.palette.text.secondary,
    },
    statusBarIconSpin: {
      animation: '$statusBarSpin 0.9s linear infinite',
    },
    statusBarBadgeTransparent: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    statusBarBadgeIconOk: {
      color: theme.palette.success.main,
    },
    statusBarButton: {
      borderRadius: 6,
      padding: theme.spacing(0.75, 1),
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.black, isLight ? 0.06 : 0.18),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.common.black, isLight ? 0.12 : 0.26),
      },
      '&.Mui-focusVisible': {
        backgroundColor: alpha(theme.palette.common.black, isLight ? 0.08 : 0.2),
      },
    },
    orderYaml: {
      margin: 0,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    orderNullValue: {
      color: theme.palette.text.secondary,
      opacity: 0.4,
    },
    '@keyframes statusBarSpin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
  });
});

const useMessageStyles = makeStyles((theme: Theme) =>
{
  const isLight = theme.palette.type === 'light';

  return ({
    messagesContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    messageRow: {
      display: 'flex',
    },
    messageRowUser: {
      justifyContent: 'flex-end',
    },
    messageRowAssistant: {
      justifyContent: 'flex-start',
    },
    messageBubble: {
      maxWidth: '80%',
      padding: theme.spacing(1, 1.5),
      borderRadius: 14,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    messageBubbleUser: {
      backgroundColor: isLight ? alpha(blue[500], 0.12) : alpha(blue[300], 0.2),
    },
    messageBubbleAssistant: {
      backgroundColor: isLight ? alpha(theme.palette.common.black, 0.06) : alpha(theme.palette.common.white, 0.12),
    },
    markdownContent: {
      lineHeight: '18px',
      whiteSpace: 'normal',
      '& *': {
        lineHeight: 'inherit',
        whiteSpace: 'inherit',
      },
      '& p': {
        margin: '4px 0',
      },
      '& p:empty': {
        display: 'none',
      },
      '& p + p': {
        marginTop: 6,
      },
      '& ul, & ol': {
        margin: '4px 0',
        padding: 0,
        paddingLeft: theme.spacing(2),
      },
      '& li': {
        margin: '2px 0',
      },
      '& pre': {
        margin: '6px 0',
        padding: theme.spacing(1),
        borderRadius: 8,
        overflowX: 'auto',
        backgroundColor: isLight ? alpha(theme.palette.common.black, 0.08) : alpha(theme.palette.common.white, 0.14),
      },
      '& code': {
        fontFamily: 'monospace',
        fontSize: '0.85em',
        backgroundColor: isLight ? alpha(theme.palette.common.black, 0.08) : alpha(theme.palette.common.white, 0.14),
        padding: '0 4px',
        borderRadius: 4,
      },
      '& pre code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
      '& a': {
        color: blue[600],
        textDecoration: 'none',
      },
      '& a:hover': {
        textDecoration: 'underline',
      },
      '& blockquote': {
        margin: '6px 0',
        padding: theme.spacing(0.25, 1),
        borderLeft: `3px solid ${alpha(blue[500], 0.5)}`,
        color: theme.palette.text.secondary,
      },
      '& h1, & h2, & h3, & h4, & h5, & h6': {
        margin: '8px 0 4px',
        fontWeight: 600,
        lineHeight: '20px',
      },
    },
    messageTimestamp: {
      marginTop: 2,
      fontSize: 11,
      opacity: 0.6,
    }
  });
});

const useComposerStyles = makeStyles((theme: Theme) =>
{
  const isLight = theme.palette.type === 'light';

  return ({
    newChatMessageBox: {
      border: 0,
      paddingLeft: theme.spacing(1),
      borderRadius: 20,
      backgroundColor: isLight ? alpha(theme.palette.common.black, 0.05) : alpha(theme.palette.common.white, 0.15),
      width: '100%',
    },
    iconButtonBase: {
      margin: -8,
      padding: 8,
    },
    attachFileIconButton: {
      marginLeft: -12,
      marginRight: 4,
    },
    attachFileButton: {
      transform: 'rotate(-135deg)',
    },
    sendMessageButton: {
      marginRight: -4,
    },
    messageInput: {
      padding: 8,
    }
  });
});

function AssistantMarkdown(props: { content: string })
{
  const classes = useMessageStyles();

  return (
    <ReactMarkdown
      className={classes.markdownContent}
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ node, color, ...linkProps }) => (
          <Link component="a" {...linkProps} target="_blank" rel="noopener noreferrer" />
        ),
      }}
    >
      {props.content}
    </ReactMarkdown>
  );
}

function MessageBubble(props: { message: ChatMessage })
{
  const classes = useMessageStyles();
  const isUser = props.message.role === 'user';

  return (
    <div className={`${classes.messageRow} ${isUser ? classes.messageRowUser : classes.messageRowAssistant}`}>
      <div className={`${classes.messageBubble} ${isUser ? classes.messageBubbleUser : classes.messageBubbleAssistant}`}>
        {isUser
          ? <Typography variant="body2">{props.message.content}</Typography>
          : <AssistantMarkdown content={props.message.content} />}
        <div className={classes.messageTimestamp}>{new Date(props.message.timestamp).toLocaleTimeString()}</div>
      </div>
    </div>
  );
}

function StreamingMessageBubble(props: { content: string })
{
  const classes = useMessageStyles();

  return (
    <div className={`${classes.messageRow} ${classes.messageRowAssistant}`}>
      <div className={`${classes.messageBubble} ${classes.messageBubbleAssistant}`}>
        <AssistantMarkdown content={props.content} />
      </div>
    </div>
  );
}

function MessageList(props: { messages: ChatMessage[]; streamingMessage: string | null; isThreadReady: boolean; loadingText: string })
{
  const classes = useMessageStyles();

  return (
    <div className={classes.messagesContainer}>
      {!props.isThreadReady && (
        <Typography variant="body2" color="textSecondary">
          {props.loadingText}
        </Typography>
      )}
      {props.messages.map(message => (
        <MessageBubble key={message.id} message={message} />
      ))}
      {props.streamingMessage && (
        <StreamingMessageBubble content={props.streamingMessage} />
      )}
    </div>
  );
}

type MessageComposerProps = {
  messageInput: ReturnType<typeof useInput>;
  sendingMessage: boolean;
  isThreadReady: boolean;
  onSend: () => void;
  onPdfUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  messageInputRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  placeholder: string;
};

function MessageComposer(props: MessageComposerProps)
{
  const classes = useComposerStyles();

  return (
    <Paper className={classes.newChatMessageBox} elevation={0} variant="outlined">
      <input
        type="file"
        ref={props.fileInputRef}
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={props.onPdfUpload}
      />
      <InputBase
        {...props.messageInput.props}
        fullWidth
        multiline
        disabled={false}
        placeholder={props.placeholder}
        className={classes.messageInput}
        inputProps={{ maxLength: 500 }}
        inputRef={props.messageInputRef}
        onKeyDown={e =>
        {
          if(e.key === 'Enter' && !e.shiftKey)
          {
            e.preventDefault();
            if(!props.sendingMessage && props.messageInput.props.value && props.isThreadReady) props.onSend();
          }
        }}
        startAdornment={
          <IconButton size="small" disabled={props.sendingMessage || !props.isThreadReady} onClick={() => props.fileInputRef.current?.click()} color="default" className={`${classes.iconButtonBase} ${classes.attachFileIconButton}`}>
            <AttachFileIcon fontSize="medium" className={classes.attachFileButton} />
          </IconButton>
        }
        endAdornment={
          <IconButton size="small" disabled={!props.messageInput.props.value || props.sendingMessage || !props.isThreadReady} onClick={props.onSend} color="primary" className={`${classes.iconButtonBase} ${classes.sendMessageButton}`} >
            {props.sendingMessage && Boolean(props.messageInput.props.value)
              ? <CircularProgress size={24} />
              : <SendChatMessageIcon fontSize="medium" />}
          </IconButton>
        }
      />
    </Paper>
  );
}

type DraftDialogProps = {
  open: boolean;
  onClose: () => void;
  orderLines: { text: string; isValue: boolean; isNull: boolean }[][];
  hasOrder: boolean;
  missingFields: string[];
  translateMissingField: (field: string) => string;
  title: string;
  missingFieldsLabel: string;
  showEmptyFields: boolean;
  onToggleEmptyFields: (show: boolean) => void;
  showEmptyFieldsLabel: string;
  canToggleEmptyFields: boolean;
  submitLabel: string;
  canSubmit: boolean;
  onSubmit: () => void;
};

function DraftDialog(props: DraftDialogProps)
{
  const classes = useAssistantStyles();

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">{props.title}</Typography>
        {props.canToggleEmptyFields && (
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={props.showEmptyFields}
                onChange={event => props.onToggleEmptyFields(event.target.checked)}
              />
            }
            label={props.showEmptyFieldsLabel}
          />
        )}
        <IconButton className={classes.closeButton} onClick={props.onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {props.missingFields.length > 0 && (
          <div style={{ marginBottom: props.hasOrder ? 12 : 0 }}>
            <Typography variant="subtitle2" gutterBottom>{props.missingFieldsLabel}</Typography>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {props.missingFields.map(field => (
                <li key={field}>
                  <Typography variant="body2">{props.translateMissingField(field)}</Typography>
                </li>
              ))}
            </ul>
          </div>
        )}
        {props.hasOrder && (
          <pre className={classes.orderYaml}>
            <code>
              {props.orderLines.map((line, index) =>
              {
                const isNullLine = line.length === 1 && line[0].isNull;
                return (
                  <span key={`yaml-line-${index}`} className={isNullLine ? classes.orderNullValue : undefined}>
                    {line.map((segment, segmentIndex) =>
                    {
                      if(segment.isNull)
                      {
                        return (
                          <em key={`yaml-line-${index}-seg-${segmentIndex}`}>
                            {segment.text}
                          </em>
                        );
                      }
                      if(segment.isValue && !segment.isNull)
                      {
                        return (
                          <strong key={`yaml-line-${index}-seg-${segmentIndex}`}>
                            {segment.text}
                          </strong>
                        );
                      }
                      return (
                        <span key={`yaml-line-${index}-seg-${segmentIndex}`}>
                          {segment.text}
                        </span>
                      );
                    })}
                    {index < props.orderLines.length - 1 ? '\n' : ''}
                  </span>
                );
              })}
            </code>
          </pre>
        )}
      </DialogContent>
      {props.hasOrder && (
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            disabled={!props.canSubmit}
            onClick={props.onSubmit}
          >
            {props.submitLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

type SubmitConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
};

function SubmitConfirmDialog(props: SubmitConfirmDialogProps)
{
  const classes = useAssistantStyles();

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="xs" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">{props.title}</Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {props.message}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onConfirm} color="default">
          {props.confirmLabel}
        </Button>
        <Button onClick={props.onClose} color="primary" variant="contained" autoFocus>
          {props.cancelLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Assistant(props: {
  open: boolean;
  onClose: () => void;
})
{
  const { t, i18n } = useTranslation();
  const classes = useAssistantStyles();
  const { open, onClose } = props;

  const messageInput = useInput();
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [showEmptyFields, setShowEmptyFields] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const messageInputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const { threadId, isThreadReady, messages, streamingMessage, addMessage, draftState } = useAssistantRealtime({
    open,
    errorFallback: t('partnerPortal.assistant.error')
  });
  const [sendAssistantMessageMutation] = useSendAssistantMessageMutation();
  const hasAssistantResponse = messages.some(message => message.role === 'assistant' || message.role === 'system');
  const hasAssistantActivity = hasAssistantResponse || Boolean(streamingMessage);
  const isProcessing = Boolean(streamingMessage);
  const isSendDisabled = sendingMessage || isProcessing;
  const isOrderOk = draftState.missingFields.length === 0;
  const hasOrder = Boolean(draftState.order) || draftState.missingFields.length > 0;
  const showOkBadge = !isProcessing && Boolean(draftState.order) && isOrderOk;
  const hiddenOrderKeys = useMemo(() => [
    'PartnerId',
    'PartnerContactId',
    'LocationId',
    'PostcodeId',
    'CountryId',
    'PartnerId',
    'HandlingInstructionId',
    'UnitId',
    'CurrencyId'
  ], []);
  const translateOrderLabel = useCallback((path: string, key: string): string =>
  {
    const baseKey = `partnerPortal.assistant.orderSchema${path ? `.${path}` : ''}.${key}`;
    const labelKey = `${baseKey}._label`;
    const hasLabel = i18n.exists(labelKey);
    const hasBase = i18n.exists(baseKey);

    if(hasLabel) return t(labelKey);
    if(hasBase) return t(baseKey);
    return key;
  }, [i18n, t]);
  const translateMissingField = useCallback((field: string): string =>
  {
    if(!field) return field;

    const normalized = field.replace(/\[\d+\]/g, '');
    const segments = normalized.split('.').filter(Boolean);
    if(segments.length === 0) return field;

    const translatedSegments: string[] = [];
    let parentPath = '';

    for(const segment of segments)
    {
      const baseKey = `partnerPortal.assistant.orderSchema${parentPath ? `.${parentPath}` : ''}.${segment}`;
      const labelKey = `${baseKey}._label`;
      const hasLabel = i18n.exists(labelKey);
      const hasBase = i18n.exists(baseKey);
      const translated = hasLabel
        ? t(labelKey)
        : (hasBase ? t(baseKey) : segment);

      translatedSegments.push(translated);
      parentPath = parentPath ? `${parentPath}.${segment}` : segment;
    }

    return translatedSegments.join(' / ');
  }, [i18n, t]);
  const orderLines = useMemo(() =>
  {
    if(!draftState.order) return [] as { text: string; isValue: boolean; isNull: boolean }[][];

    const yesText = t('partnerPortal.assistant.valueYes', 'igen');
    const noText = t('partnerPortal.assistant.valueNo', 'nem');
    const notProvidedText = t('partnerPortal.assistant.valueNotProvided', 'nincs megadva');
    const preferredOrder: Record<string, string[]> = {
      '': [
        'OriginPartner',
        'OriginLocation',
        'OriginContact',
        'DestinationPartner',
        'DestinationLocation',
        'DestinationContact',
        'ExternalNumbers',
        'OrderItems',
        'Remarks'
      ],
      'OrderItems': [
        'Name',
        'ContainerNumber',
        'Quantity',
        'QuantityUnit',
        'SecondaryQuantity',
        'Weight',
        'Volume',
        'LoadingMeter',
        'Sizes',
        'Value',
        'EKAERInfo',
        'RequestsInsurance',
        'ExchangePackaging',
        'HandlingInstruction',
        'PalletSpaces',
      ]
    };
    const formatValue = (value: unknown): { text: string; isNull: boolean } =>
    {
      if(value === null || value === undefined)
      {
        return { text: notProvidedText, isNull: true };
      }

      if(typeof value === 'boolean')
      {
        return { text: value ? yesText : noText, isNull: false };
      }

      return { text: String(value), isNull: false };
    };

    const hasValue = (value: unknown): boolean =>
    {
      if(value === null || value === undefined) return false;
      if(Array.isArray(value)) return value.some(item => hasValue(item));
      if(typeof value === 'object') return Object.values(value as Record<string, unknown>).some(item => hasValue(item));
      return true;
    };

    const lines: { text: string; isValue: boolean; isNull: boolean }[][] = [];
    const orderEntries = (entries: [string, unknown][], path: string): [string, unknown][] =>
    {
      const preferredKeys = preferredOrder[path];
      if(!preferredKeys) return entries;

      const ranked = new Map(preferredKeys.map((key, index) => [key, index]));
      return entries.slice().sort((a, b) =>
      {
        const rankA = ranked.has(a[0]) ? ranked.get(a[0])! : Number.MAX_SAFE_INTEGER;
        const rankB = ranked.has(b[0]) ? ranked.get(b[0])! : Number.MAX_SAFE_INTEGER;
        if(rankA !== rankB) return rankA - rankB;
        return 0;
      });
    };
    const getChildPath = (parentPath: string, labelValue?: string): string =>
    {
      if(!labelValue) return parentPath;
      return parentPath ? `${parentPath}.${labelValue}` : labelValue;
    };
    const addLines = (value: unknown, indent: number, label?: string, parentPath: string = '') =>
    {
      const prefix = '  '.repeat(indent);
      const currentPath = getChildPath(parentPath, label);

      if(Array.isArray(value))
      {
        const displayLabel = label ? translateOrderLabel(parentPath, label) : undefined;
        if(label)
        {
          if(!showEmptyFields && !hasValue(value)) return;
          lines.push([
            { text: `${prefix}${displayLabel}:`, isValue: false, isNull: false }
          ]);
        }

        value.forEach(item =>
        {
          if(!showEmptyFields && !hasValue(item)) return;
          if(item && typeof item === 'object')
          {
            lines.push([
              { text: `${prefix}  -`, isValue: false, isNull: false }
            ]);
            addLines(item, indent + 2, undefined, currentPath);
          }
          else
          {
            const formatted = formatValue(item);
            lines.push([
              { text: `${prefix}  - `, isValue: false, isNull: false },
              { text: formatted.text, isValue: true, isNull: formatted.isNull }
            ]);
          }
        });
        return;
      }

      if(value && typeof value === 'object')
      {
        const entries = orderEntries(
          Object.entries(value as Record<string, unknown>).filter(([key]) => !hiddenOrderKeys.includes(key)),
          currentPath
        );
        const displayLabel = label ? translateOrderLabel(parentPath, label) : undefined;
        if(label)
        {
          if(!showEmptyFields && !hasValue(value)) return;
          lines.push([
            { text: `${prefix}${displayLabel}:`, isValue: false, isNull: false }
          ]);
        }

        entries.forEach(([key, entryValue]) =>
        {
          if(!showEmptyFields && !hasValue(entryValue)) return;
          addLines(entryValue, indent + (label ? 1 : 0), key, currentPath);
        });
        return;
      }

      if(label)
      {
        const displayLabel = translateOrderLabel(parentPath, label);
        const formatted = formatValue(value);
        if(!showEmptyFields && formatted.isNull) return;
        lines.push([
          { text: `${prefix}${displayLabel}: `, isValue: false, isNull: false },
          { text: formatted.text, isValue: true, isNull: formatted.isNull }
        ]);
        return;
      }

      const formatted = formatValue(value);
      if(showEmptyFields || !formatted.isNull)
      {
        lines.push([
          { text: `${prefix}`, isValue: false, isNull: false },
          { text: formatted.text, isValue: true, isNull: formatted.isNull }
        ]);
      }
    };

    addLines(draftState.order, 0);
    if(lines.length === 0 && (showEmptyFields || draftState.missingFields.length > 0))
    {
      lines.push([
        { text: notProvidedText, isValue: true, isNull: true }
      ]);
    }
    return lines;
  }, [draftState.missingFields.length, draftState.order, hiddenOrderKeys, showEmptyFields, t, translateOrderLabel]);

  const canToggleEmptyFields = useMemo(() =>
  {
    if(!draftState.order) return false;

    const hasEmptyValues = (value: unknown): boolean =>
    {
      if(value === null || value === undefined) return true;

      if(Array.isArray(value))
      {
        if(value.length === 0) return true;
        return value.some(item => hasEmptyValues(item));
      }

      if(value && typeof value === 'object')
      {
        const entries = Object.values(value as Record<string, unknown>);
        if(entries.length === 0) return true;
        return entries.some(item => hasEmptyValues(item));
      }

      return false;
    };

    return hasEmptyValues(draftState.order);
  }, [draftState.order]);

  useEffect(() =>
  {
    if(!open) return;

    const container = dialogContentRef.current;
    if(!container) return;

    requestAnimationFrame(() =>
    {
      container.scrollTop = container.scrollHeight;
    });
  }, [open, messages.length, streamingMessage, isThreadReady, draftState.missingFields.length]);

  const handlePdfUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) =>
  {
    const file = event.target.files?.[0];
    if(file && file.type === 'application/pdf')
    {
      // TODO: Implement PDF upload logic
      console.log('Uploading PDF:', file.name);
    }
    // Reset input value to allow selecting the same file again
    if(fileInputRef.current)
    {
      fileInputRef.current.value = '';
    }
  }, []);

  const sendMessage = useCallback(async () =>
  {
    if(!messageInput.props.value || !threadId) return;

    setSendingMessage(true);
    try
    {
      const userMessage = messageInput.props.value.toString();
      const timestamp = new Date().toISOString();
      const currentOrderJson = draftState.order ? JSON.stringify(draftState.order) : null;
      const currentMissingFields = draftState.missingFields.length > 0 ? draftState.missingFields : null;

      addMessage({ id: `user-${Date.now()}`, role: 'user', content: userMessage, timestamp });

      await sendAssistantMessageMutation({
        variables: {
          input: {
            ThreadId: threadId,
            Message: userMessage,
            FileIds: [],
            CurrentOrderJson: currentOrderJson,
            CurrentMissingFields: currentMissingFields,
          }
        }
      });

      messageInput.setValue("");
    } catch(error)
    {
      console.error('Error sending message:', error);
    } finally
    {
      setSendingMessage(false);
      // Refocus after re-enabling the input
      setTimeout(() => messageInputRef.current?.focus(), 0);
    }
  }, [addMessage, draftState.missingFields, draftState.order, messageInput, messageInputRef, sendAssistantMessageMutation, threadId]);

  const handleDialogClose = useCallback((event: unknown, reason?: string) =>
  {
    if(reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    onClose();
  }, [onClose]);

  const handleSubmitRequest = useCallback(() =>
  {
    setShowSubmitConfirm(true);
  }, []);

  const handleSubmitConfirm = useCallback(() =>
  {
    setShowSubmitConfirm(false);
  }, []);

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle className={classes.dialogTitle}><Typography variant="h6"> {t('partnerPortal.assistant.title')}</Typography><IconButton className={classes.closeButton} onClick={onClose}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent ref={dialogContentRef} dividers className={classes.dialogContent}>
          <DialogContentText>
            <AssistantIcon className={classes.chatMessageIcon} /> {t('partnerPortal.assistant.welcomeMessage')}
          </DialogContentText>
          <MessageList
            messages={messages}
            streamingMessage={streamingMessage}
            isThreadReady={isThreadReady}
            loadingText={t('partnerPortal.assistant.creatingThread', 'Kapcsolódás...')}
          />
          {hasAssistantActivity && (
            <div className={classes.statusBar}>
              <IconButton
                size="small"
                onClick={() => setShowDraftDialog(true)}
                aria-label="assistant draft json"
                disabled={isProcessing}
                className={classes.statusBarButton}
                disableRipple
                disableFocusRipple
              >
                <Badge
                  badgeContent={isProcessing
                    ? <CircularProgress size={14} />
                    : (showOkBadge
                      ? <CheckIcon fontSize="small" className={classes.statusBarBadgeIconOk} />
                      : draftState.missingFields.length)}
                  color="secondary"
                  invisible={!isProcessing && !showOkBadge && isOrderOk}
                  classes={isProcessing || showOkBadge ? { badge: classes.statusBarBadgeTransparent } : undefined}
                >
                  <DescriptionIcon
                    fontSize="small"
                    className={classes.statusBarIcon}
                  />
                </Badge>
                <Typography variant="caption" className={classes.statusBarLabel}>{t('partnerPortal.assistant.orderLabel')}</Typography>
              </IconButton>
            </div>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <MessageComposer
            messageInput={messageInput}
            sendingMessage={isSendDisabled}
            isThreadReady={isThreadReady}
            onSend={sendMessage}
            onPdfUpload={handlePdfUpload}
            messageInputRef={messageInputRef}
            fileInputRef={fileInputRef}
            placeholder={t('partnerPortal.assistant.newMessagePlaceholder')}
          />
        </DialogActions>
      </Dialog>
      <DraftDialog
        open={showDraftDialog}
        onClose={() => setShowDraftDialog(false)}
        orderLines={orderLines}
        hasOrder={hasOrder}
        missingFields={draftState.missingFields}
        translateMissingField={translateMissingField}
        title={t('partnerPortal.assistant.orderTitle')}
        missingFieldsLabel={t('partnerPortal.assistant.missingFields')}
        showEmptyFields={showEmptyFields}
        onToggleEmptyFields={setShowEmptyFields}
        showEmptyFieldsLabel={t('partnerPortal.assistant.showEmptyFields')}
        canToggleEmptyFields={canToggleEmptyFields}
        submitLabel={t('partnerPortal.assistant.submitOrder')}
        canSubmit={Boolean(draftState.order) && isOrderOk}
        onSubmit={handleSubmitRequest}
      />
      <SubmitConfirmDialog
        open={showSubmitConfirm}
        onClose={() => setShowSubmitConfirm(false)}
        onConfirm={handleSubmitConfirm}
        title={t('partnerPortal.assistant.submitConfirmTitle')}
        message={t('partnerPortal.assistant.submitConfirmText')}
        confirmLabel={t('partnerPortal.assistant.submitConfirmYes')}
        cancelLabel={t('partnerPortal.assistant.submitConfirmNo')}
      />
    </>
  );
}
